import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  LineChart, Line, Legend, RadarChart, Radar, PolarGrid, PolarAngleAxis, Cell,
} from "recharts";
import { Download, RefreshCw, Loader2, TrendingUp, Compass, Layers, ShieldCheck } from "lucide-react";
import client from "../../lib/api";
import { PageHeader, KpiCard, PrivacyBadge } from "./adminUi";

const ARCH_PALETTE = ["#1D5FD4", "#0E7A66", "#8A5808", "#6D4AFF", "#B3261E", "#0891B2", "#C2410C", "#4D7C0F", "#9333EA", "#0F766E"];
const pctInt = (v) => (v == null ? null : Math.round(v * 100));

export default function AdminCohortScreen() {
  const [term, setTerm] = useState("");
  const [program, setProgram] = useState("");
  const [d, setD] = useState({});
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setBusy(true); setErr("");
    const qs = new URLSearchParams();
    if (term.trim()) qs.set("term", term.trim());
    if (program.trim()) qs.set("program", program.trim());
    const q = qs.toString() ? `?${qs}` : "";
    const pq = program.trim() ? `?program=${encodeURIComponent(program.trim())}` : "";
    try {
      const [o, a, l, p, tr] = await Promise.all([
        client.get(`/cohort/overview${q}`), client.get(`/cohort/archetypes${q}`),
        client.get(`/cohort/layers${q}`), client.get(`/cohort/performance${q}`),
        client.get(`/cohort/trends${pq}`),
      ]);
      setD({ overview: o.data, archetypes: a.data, layers: l.data, performance: p.data, trends: tr.data });
    } catch (e) {
      setErr(e?.response?.status === 403 ? "You need an institution admin role to open this." : "Could not load the cohort view.");
      setD({});
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [term, program]); // eslint-disable-line

  const { overview, archetypes, layers, performance, trends } = d;
  const small = overview?.small_cohort;

  const archData = useMemo(() => (archetypes?.buckets || [])
    .filter((b) => !b.suppressed && b.count)
    .sort((x, y) => y.count - x.count).slice(0, 8)
    .map((b) => ({ name: b.label, value: b.count, code: b.code })), [archetypes]);

  const radarData = useMemo(() => (layers?.buckets || [])
    .map((b) => ({ layer: b.label, value: b.suppressed ? 0 : pctInt(b.proportion) || 0 })), [layers]);

  const trendData = useMemo(() => (trends?.terms || []).map((t) => ({
    name: t.label,
    completion: t.small_cohort ? null : pctInt(t.completion_rate),
    ready: (() => { const hi = (t.readiness || []).find((x) => x.code === "high" && !x.suppressed); return hi ? pctInt(hi.proportion) : null; })(),
  })), [trends]);

  return (
    <div data-testid="cohort-dashboard-screen">
      <PageHeader
        title="Cohort analytics"
        subtitle="The shape of your cohort at a glance. Aggregate only, held to a five student floor. No names, no individual, ever."
        actions={
          <>
            <PrivacyBadge />
            <ExportButton term={term} program={program} />
            <button className="k-btn k-btn-ghost !py-2" onClick={load} disabled={busy} data-testid="cohort-reload-btn"><RefreshCw size={14} /> Refresh</button>
          </>
        }
      />

      <div className="k-card mb-6 flex items-start gap-3" style={{ padding: "1rem 1.25rem", background: "rgba(14,122,102,0.05)", borderColor: "rgba(14,122,102,0.2)" }} data-testid="cohort-banner">
        <ShieldCheck size={18} style={{ color: "#0E7A66", marginTop: 2 }} />
        <p className="text-sm" style={{ color: "#131822", lineHeight: 1.55 }}>{overview?.disclaimer || "Aggregate only. A development tool for your cohort, not a selection or ranking tool."}</p>
      </div>

      <div className="flex flex-wrap gap-3 items-end mb-6">
        <div><label className="k-label mb-1 block">Term</label><input className="k-input" style={{ width: "11rem" }} value={term} onChange={(e) => setTerm(e.target.value)} placeholder="2026-spring" data-testid="cohort-filter-term" /></div>
        <div><label className="k-label mb-1 block">Program</label><input className="k-input" style={{ width: "11rem" }} value={program} onChange={(e) => setProgram(e.target.value)} placeholder="Computer Science" data-testid="cohort-filter-program" /></div>
        {(term || program) && <button className="k-btn k-btn-ghost" onClick={() => { setTerm(""); setProgram(""); }} data-testid="cohort-filter-clear">Clear</button>}
      </div>

      {err && <div className="k-card mb-6" style={{ padding: "1.25rem", borderColor: "rgba(179,38,30,0.3)", color: "#B3261E" }} data-testid="cohort-error">{err}</div>}
      {busy && !overview && <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin" size={20} /></div>}

      {overview && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard testId="cohort-size" label="Students in cohort" value={overview.cohort_size} />
          <KpiCard testId="cohort-completion-rate" label="Completed read" accent="#0E7A66" value={small || overview.completion_rate == null ? "n/a" : `${pctInt(overview.completion_rate)}%`} />
          <KpiCard testId="cohort-avg-time" label="Average time" accent="#8A5808" value={small || overview.avg_time_minutes == null ? "n/a" : `${overview.avg_time_minutes.toFixed(0)}m`} />
          <KpiCard testId="cohort-goal-readiness" label="Avg goal readiness" accent="#1D5FD4" value={performance?.avg_goal_readiness == null ? "n/a" : `${performance.avg_goal_readiness}%`} />
        </div>
      )}

      {small && (
        <div className="k-card" style={{ padding: "2.5rem", textAlign: "center" }} data-testid="cohort-too-small-notice">
          <p className="k-quiet" style={{ maxWidth: "34rem", margin: "0 auto", lineHeight: 1.6 }}>A cohort under {overview.k} people cannot be shown as a distribution without revealing individuals. Widen the filters or come back when more have finished.</p>
        </div>
      )}

      {overview && !small && (
        <>
          {trendData.length >= 2 && !term.trim() && (
            <div className="k-card mb-6" style={{ padding: "1.5rem" }} data-testid="cohort-trends-section">
              <div className="flex items-center gap-2 text-sm font-medium mb-1" style={{ color: "#131822" }}><TrendingUp size={16} /> Term over term</div>
              {trends.headline && <p className="klareet-serif mb-4" style={{ fontSize: "1.05rem", color: "#131822", lineHeight: 1.45 }} data-testid="cohort-trends-headline">{trends.headline}</p>}
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trendData} margin={{ left: 4, right: 12, top: 8 }}>
                  <CartesianGrid stroke="rgba(19,24,34,0.06)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontFamily: "Archivo", fontSize: 12, fill: "rgba(19,24,34,0.7)" }} />
                  <YAxis unit="%" domain={[0, 100]} tick={{ fontFamily: "JetBrains Mono", fontSize: 11, fill: "rgba(19,24,34,0.6)" }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid rgba(19,24,34,0.1)", fontFamily: "JetBrains Mono", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontFamily: "Archivo", fontSize: 12 }} />
                  <Line type="monotone" dataKey="completion" name="Completed read" stroke="#0E7A66" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                  <Line type="monotone" dataKey="ready" name="Ready to move" stroke="#1D5FD4" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <div className="k-card" style={{ padding: "1.5rem" }} data-testid="cohort-archetypes-section">
              <div className="flex items-center gap-2 text-sm font-medium mb-4" style={{ color: "#131822" }}><Compass size={16} /> Who your students are</div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={archData} layout="vertical" margin={{ left: 8, right: 16 }}>
                  <CartesianGrid horizontal={false} stroke="rgba(19,24,34,0.06)" />
                  <XAxis type="number" tick={{ fontFamily: "JetBrains Mono", fontSize: 11, fill: "rgba(19,24,34,0.6)" }} />
                  <YAxis type="category" dataKey="name" width={130} tick={{ fontFamily: "Archivo", fontSize: 11, fill: "#131822" }} />
                  <Tooltip cursor={{ fill: "rgba(19,24,34,0.03)" }} contentStyle={{ borderRadius: 8, border: "1px solid rgba(19,24,34,0.1)", fontFamily: "JetBrains Mono", fontSize: 12 }} />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={18}>
                    {archData.map((entry, i) => <Cell key={entry.code} fill={ARCH_PALETTE[i % ARCH_PALETTE.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="k-card" style={{ padding: "1.5rem" }} data-testid="cohort-layers-section">
              <div className="flex items-center gap-2 text-sm font-medium mb-4" style={{ color: "#131822" }}><Layers size={16} /> Strongest layer spread</div>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={radarData} outerRadius={110}>
                  <PolarGrid stroke="rgba(19,24,34,0.12)" />
                  <PolarAngleAxis dataKey="layer" tick={{ fontFamily: "Archivo", fontSize: 11, fill: "rgba(19,24,34,0.75)" }} />
                  <Radar dataKey="value" stroke="#1D5FD4" fill="#1D5FD4" fillOpacity={0.35} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid rgba(19,24,34,0.1)", fontFamily: "JetBrains Mono", fontSize: 12 }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {performance && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <BandCard title="Readiness to act" buckets={performance.readiness} testId="cohort-readiness-card" />
              <BandCard title="Engagement in the read" buckets={performance.engagement} testId="cohort-engagement-card" />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BandCard({ title, buckets, testId }) {
  const data = (buckets || []).map((b) => ({ name: b.label, value: b.suppressed ? 0 : Math.round((b.proportion || 0) * 100), suppressed: b.suppressed }));
  return (
    <div className="k-card" style={{ padding: "1.5rem" }} data-testid={testId}>
      <div className="text-sm font-medium mb-4" style={{ color: "#131822" }}>{title}</div>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} margin={{ left: 4, right: 12 }}>
          <CartesianGrid vertical={false} stroke="rgba(19,24,34,0.06)" />
          <XAxis dataKey="name" tick={{ fontFamily: "Archivo", fontSize: 12, fill: "#131822" }} />
          <YAxis unit="%" domain={[0, 100]} tick={{ fontFamily: "JetBrains Mono", fontSize: 11, fill: "rgba(19,24,34,0.6)" }} />
          <Tooltip cursor={{ fill: "rgba(19,24,34,0.03)" }} contentStyle={{ borderRadius: 8, border: "1px solid rgba(19,24,34,0.1)", fontFamily: "JetBrains Mono", fontSize: 12 }} />
          <Bar dataKey="value" fill="#0E7A66" radius={[4, 4, 0, 0]} barSize={44} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ExportButton({ term, program }) {
  const onClick = async () => {
    const qs = new URLSearchParams();
    if (term.trim()) qs.set("term", term.trim());
    if (program.trim()) qs.set("program", program.trim());
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("klareet.access");
    try {
      const resp = await fetch(`${base}/api/cohort/export.csv${qs.toString() ? "?" + qs : ""}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) return;
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      const disp = resp.headers.get("content-disposition") || "";
      const m = disp.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : "klareet-cohort-aggregate.csv";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(a.href);
    } catch (_e) { /* silent */ }
  };
  return <button className="k-btn k-btn-ghost !py-2" onClick={onClick} data-testid="cohort-export-csv-btn"><Download size={14} /> Export CSV</button>;
}
